import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRightLeft, Plus, MoreVertical, Download, Trash2, Eye, CheckCircle, AlertCircle, Clock, FileText, Database, Upload } from 'lucide-react';
import { NavBar } from '../components/NavBar';
import { Button } from '../components/SubscriptionAppButton';
import { StatCard } from '../components/StatCard';
import { Badge } from '../components/SubscriptionAppBadge';
import { Card } from '../components/SubscriptionAppCard';
// Mock Data
const MOCK_MIGRATIONS = [{
  id: '1',
  name: 'Recharge Subscriber Import',
  source: 'Recharge',
  records: 847,
  imported: 831,
  errors: 16,
  status: 'Completed',
  date: '12 Jan 2026'
}, {
  id: '2',
  name: 'WineHub Legacy Import',
  source: 'WineHub',
  records: 312,
  imported: 312,
  errors: 0,
  status: 'Completed',
  date: '15 Nov 2025'
}, {
  id: '3',
  name: 'Manual CSV Upload',
  source: 'CSV',
  records: 88,
  imported: 0,
  errors: 0,
  status: 'Draft',
  date: '10 Oct 2025'
}];
export function MigrationHubPage() {
  const navigate = useNavigate();
  const [isMigrating, setIsMigrating] = useState(false); // Toggle for demo
  const [migrations, setMigrations] = useState(MOCK_MIGRATIONS);
  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'Completed':
        return 'success';
      case 'In Progress':
        return 'warning';
      case 'Draft':
        return 'info';
      case 'Failed':
        return 'error';
      default:
        return 'default';
    }
  };
  const getSourceIcon = (source: string) => {
    switch (source) {
      case 'Recharge':
        return <Database className="w-4 h-4 text-[#64748B]" data-id="element-7245" />;
      case 'WineHub':
        return <Database className="w-4 h-4 text-[#64748B]" data-id="element-7246" />;
      case 'CSV':
        return <FileText className="w-4 h-4 text-[#64748B]" data-id="element-7247" />;
      default:
        return <Database className="w-4 h-4 text-[#64748B]" data-id="element-7248" />;
    }
  };
  return <div className="bg-[#F1F5F9] min-h-full p-6" data-id="element-7249">
      {/* NavBar */}
      <div className="mb-6" data-id="element-7250">
        <NavBar tabs={[]} title="Migration" data-id="element-7251" />
      </div>

      {/* Page Header */}
      <div className="mb-6 flex flex-col md:flex-row md:items-center justify-between gap-4" data-id="element-7252">
        <p className="text-sm text-[#64748B]" data-id="element-7253">
          Import subscribers from other platforms and preserve their history.
        </p>
        <div className="flex items-center gap-3" data-id="element-7254">
          {/* Demo Toggle */}
          <label className="flex items-center gap-2 cursor-pointer mr-4" data-id="element-7255">
            <input type="checkbox" checked={isMigrating} onChange={e => setIsMigrating(e.target.checked)} className="w-4 h-4 rounded border-[#CBD5E1] text-[#0EA5E9] focus:ring-[#0EA5E9]" data-id="element-7256" />
            <span className="text-sm text-[#64748B]" data-id="element-7257">
              Demo: Show In Progress
            </span>
          </label>

          <Button variant="primary" iconLeft={<Plus className="w-4 h-4" data-id="element-7259" />} onClick={() => navigate('/management/migration/new')} data-id="element-7258">
            New Migration
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8" data-id="element-7260">
        <StatCard label="Total Migrations" value="3" secondaryValue="all time" data-id="element-7261" />
        <StatCard label="Records Imported" value="1,247" secondaryValue="across all migrations" data-id="element-7262" />
        <StatCard label="Last Migration" value="12 Jan 2026" secondaryValue="Recharge Import" data-id="element-7263" />
        <StatCard label="Success Rate" value="98.2%" trend={{
        value: '',
        isPositive: true
      }} // Using trend for color only
      data-id="element-7264" />
      </div>

      {/* In Progress Alert Banner */}
      {isMigrating && <div className="mb-8 bg-white border border-[#E2E8F0] rounded-lg p-6 shadow-sm" data-id="element-7265">
          <div className="flex items-start gap-4" data-id="element-7266">
            <div className="w-10 h-10 rounded-full bg-[#E0F2FE] flex items-center justify-center flex-shrink-0 animate-pulse" data-id="element-7267">
              <Upload className="w-5 h-5 text-[#0EA5E9]" data-id="element-7268" />
            </div>
            <div className="flex-1" data-id="element-7269">
              <div className="flex items-center justify-between mb-2" data-id="element-7270">
                <h3 className="text-base font-semibold text-[#0F172A]" data-id="element-7271">
                  Importing 847 records...
                </h3>
                <button onClick={() => navigate('/management/migration/1')} className="text-sm font-medium text-[#0EA5E9] hover:text-[#0284C7]" data-id="element-7272">
                  View Details
                </button>
              </div>
              <p className="text-sm text-[#64748B] mb-3" data-id="element-7273">
                623 of 847 processed • ~4 min remaining
              </p>
              <div className="w-full bg-[#F1F5F9] rounded-full h-2 overflow-hidden" data-id="element-7274">
                <div className="bg-[#0EA5E9] h-2 rounded-full transition-all duration-500" style={{
              width: '73%'
            }} data-id="element-7275" />
              </div>
            </div>
          </div>
        </div>}

      {/* Migration History Table */}
      {migrations.length > 0 ? <Card padding="none" className="overflow-hidden" data-id="element-7276">
          <div className="px-6 py-4 border-b border-[#E2E8F0]" data-id="element-7277">
            <h2 className="text-lg font-semibold text-[#0F172A]" data-id="element-7278">
              Migration History
            </h2>
          </div>
          <div className="overflow-x-auto" data-id="element-7279">
            <table className="w-full" data-id="element-7280">
              <thead className="bg-[#F8FAFC]" data-id="element-7281">
                <tr data-id="element-7282">
                  <th className="px-6 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-7283">
                    Migration Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-7284">
                    Source
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-7285">
                    Records
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-7286">
                    Imported
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-7287">
                    Errors
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-7288">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-7289">
                    Date
                  </th>
                  <th className="px-6 py-3 w-10" data-id="element-7290"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E2E8F0]" data-id="element-7291">
                {migrations.map(migration => <tr key={migration.id} className="hover:bg-[#F8FAFC]" data-id="element-7292">
                    <td className="px-6 py-4" data-id="element-7293">
                      <button onClick={() => navigate(`/management/migration/${migration.id}`)} className="text-sm font-medium text-[#0F172A] hover:text-[#0EA5E9] hover:underline text-left" data-id="element-7294">
                        {migration.name}
                      </button>
                    </td>
                    <td className="px-6 py-4" data-id="element-7295">
                      <div className="flex items-center gap-2" data-id="element-7296">
                        {getSourceIcon(migration.source)}
                        <span className="text-sm text-[#475569]" data-id="element-7297">
                          {migration.source}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right text-sm text-[#475569]" data-id="element-7298">
                      {migration.records}
                    </td>
                    <td className="px-6 py-4 text-right text-sm text-[#475569]" data-id="element-7299">
                      {migration.imported}
                    </td>
                    <td className="px-6 py-4 text-right" data-id="element-7300">
                      <span className={`text-sm font-medium ${migration.errors > 0 ? 'text-[#EF4444]' : 'text-[#94A3B8]'}`} data-id="element-7301">
                        {migration.errors}
                      </span>
                    </td>
                    <td className="px-6 py-4" data-id="element-7302">
                      <Badge variant={getStatusBadgeVariant(migration.status)} data-id="element-7303">
                        {migration.status}
                      </Badge>
                    </td>
                    <td className="px-6 py-4 text-sm text-[#64748B]" data-id="element-7304">
                      {migration.date}
                    </td>
                    <td className="px-6 py-4" data-id="element-7305">
                      <button className="text-[#94A3B8] hover:text-[#0F172A] transition-colors" data-id="element-7306">
                        <MoreVertical className="w-4 h-4" data-id="element-7307" />
                      </button>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
          {/* Pagination */}
          <div className="px-6 py-4 border-t border-[#E2E8F0] flex items-center justify-between" data-id="element-7308">
            <span className="text-sm text-[#64748B]" data-id="element-7309">
              Showing 1-{migrations.length} of {migrations.length} migrations
            </span>
            <div className="flex gap-2" data-id="element-7310">
              <Button variant="secondary" size="sm" disabled data-id="element-7311">
                Previous
              </Button>
              <Button variant="secondary" size="sm" disabled data-id="element-7312">
                Next
              </Button>
            </div>
          </div>
        </Card> /* Empty State */ : <div className="flex flex-col items-center justify-center py-16 bg-white border border-[#E2E8F0] rounded-lg shadow-sm" data-id="element-7313">
          <div className="w-16 h-16 bg-[#F1F5F9] rounded-full flex items-center justify-center mb-4" data-id="element-7314">
            <ArrowRightLeft className="w-8 h-8 text-[#94A3B8]" data-id="element-7315" />
          </div>
          <h3 className="text-lg font-semibold text-[#0F172A] mb-2" data-id="element-7316">
            No migrations yet
          </h3>
          <p className="text-sm text-[#64748B] max-w-md text-center mb-6" data-id="element-7317">
            Import your existing subscribers from another platform. We'll
            preserve their join dates, order history, and tenure so your
            analytics work from day one.
          </p>
          <Button variant="primary" iconLeft={<Plus className="w-4 h-4" data-id="element-7319" />} onClick={() => navigate('/management/migration/new')} data-id="element-7318">
            Start Your First Migration
          </Button>
        </div>}
    </div>;
}